import React, { useCallback } from 'react';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import type { UploadRejectionData } from '@atlaskit/media-picker/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/company-hub-common/controllers/sentry-utils';
import {
	ProductDependenciesContext,
	type ProductDependenciesProviderResponse,
} from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { HubErrorLogger } from '@atlassian/company-hub-common/hub-error-logger';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { useHubContext } from '@atlassian/company-hub-common/utils';

interface createMediaProviderProps {
	onUploadRejection?: (data: UploadRejectionData) => boolean;
}

export const CommonHubExtensionsDependenciesProvider = ({
	children,
}: React.PropsWithChildren<unknown>): React.JSX.Element => {
	const { cloudId, cloudUrl, pageMediaConfig, hubMediaConfig, env, branding, user, hubType } =
		useHubContext();
	const { hoistedProjectKey } = env;

	// Advanced theming v2: read derived theming literals from HubThemingContext.
	// The provider is mounted at the HubAppShell level so every consumer reaches
	// the same memoised palette / defaults / swatch map — and the values are
	// SSR-safe hex literals (no --hub-* CSS vars).
	// useHubTheming returns undefined outside the provider, but this provider is
	// only mounted by help-center inside HubBootstrap → provider is in tree.
	// NOTE: must run BEFORE the pageMediaConfig guard below — Rules of Hooks
	// require the same number of hook calls on every render.
	const { defaultParameters, brandBackgroundColorMap } = useHubTheming()!;

	// When the fix flag is ON, `pageMediaConfig` is provided by the `Hub`
	// component (via `hubQuery`) via a nested `HubContext.Provider`. When the
	// flag is OFF it comes from the bootstrap. Either way it must be present
	// by the time this provider renders; if it is missing the consumer is
	// mounted in an unsupported position.
	if (!pageMediaConfig) {
		throw new Error(
			'CommonHubExtensionsDependenciesProvider requires `pageMediaConfig` on the HubContext. ' +
				'Ensure this provider is rendered as a descendant of the `Hub` component.',
		);
	}

	const authProvider = useCallback(
		() =>
			Promise.resolve({
				asapIssuer: pageMediaConfig.asapIssuer,
				token: pageMediaConfig.mediaToken,
				baseUrl: pageMediaConfig.mediaUrl,
			}),
		[pageMediaConfig],
	);

	const createEditorMediaProvider = useCallback(
		async ({ onUploadRejection }: createMediaProviderProps): Promise<MediaProvider> => {
			const mediaProvider: MediaProvider = {
				uploadParams: {
					collection: pageMediaConfig.mediaCollectionName,
					onUploadRejection,
				},
				viewMediaClientConfig: {
					authProvider,
				},
				uploadMediaClientConfig: {
					authProvider,
				},
			};

			return mediaProvider;
		},
		[authProvider, pageMediaConfig],
	);

	const useMediaConfig = useCallback(
		({ imageSrc }: { imageSrc: string }) => {
			return {
				authProvider,
				collection: pageMediaConfig.mediaCollectionName,
				fileId: imageSrc,
			};
		},
		[authProvider, pageMediaConfig],
	);

	const contextValue: ProductDependenciesProviderResponse = {
		smartCardProviderComponent: undefined,
		isTargetAudience: () => false,
		useSegmentationTargetGroups: () => ({}),
		isCompanyHub: true, // need to pass this true to enable company hub specific extensions
		// Help-Hub-only signal. Derived here from the hub context, which is
		// help-center-only (this provider is never mounted outside a HubContext).
		// The Confluence company hub uses ConfluenceDependenciesProvider and so
		// leaves this false; CSM help centers have hubType !== 'HELP_HUB'.
		isHelpHub: hubType === 'HELP_HUB',
		enableLinkInputInMediaPicker: true,
		cloudId,
		cloudUrl,
		hoistedProjectKey,
		helpCenterAri: env.helpCenterAri,
		isAdminHubAIEnabled: false,
		linkPickerConfig: { linkPickerPlugins: [] },
		userId: fg('fix_user_context_in_hubs') ? user.id : '',
		customMediaConfig: hubMediaConfig,
		createEditorMediaProvider,
		useMediaConfig,
		branding: {
			...branding,
			defaultParameters,
			hideDefaultColorPalette: !!branding?.colors?.primary,
			brandBackgroundColorMap,
		},
		hideExtensionBorders: true,
		showConfigPreambleSupportLink: false,
		enableTargeting: false,
		enableUnifiedSearchFeatures: fg('enable_dynamic_cards_for_gday'),
		enableSmartCardsFields: false,
		experienceStopComponent: HubErrorLogger,
		submitError: logException,
	};

	return (
		<ProductDependenciesContext.Provider value={contextValue}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};
